import { Alignment, Api, CompanySettings, CurrencySettings, LicenseTester, ModelRow } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ClickEvent, Component, DataSourceExecutionEvent, DataSourceMode, DataSourceValidationEvent, Label, Layout,
    Panel, Snackbar, Tab, Table, TableRowMode
} from "@mcleod/components";
import { TransitionOptions } from "@mcleod/components/src/base/TransitionOptions";
import { CommonTooltips } from "@mcleod/common";
import { AutogenLayoutViewEdiBalanceDue } from "./autogen/AutogenLayoutViewEdiBalanceDue";
import { EdiMapErrors } from "./EdiMapErrors";
import { EdiRawDataSlideout } from "./EdiRawDataSlideout";
import { getReadyToSendDropdownItems } from "./ViewEdiBilling";
import { openEdiBalanceDueSlideout } from "./EdiBillingUtil";
import { TransmissionStatusQuickInfo } from "./TransmissionStatusQuickInfo"
import { setBatchCodeTooltip } from "./EdiUtility";
import { EdiProfileBillingQuickInfo } from "./EdiProfileBillingQuickInfo";
import { reloadEdiBilling } from "./EdiBillingUtil";
import { ViewEdiBillingDetail } from "./ViewEdiBillingDetail";

export class ViewEdiBalanceDue extends AutogenLayoutViewEdiBalanceDue {

    private chargePanelExpanded: boolean = true;
    private taxPanelExpanded: boolean = true;
    private _openedFromSlideout: boolean = false;
    private _panelEdiBalanceDueExpanded: boolean = true;
    private _newBatch: number = 0;

    override onLoad(): void {
        if (this.openedFromSlideout) {
            this.expand();
        }
        else {
            this.collapse();
        }
        this.styleTab(this.tabErrors);
        this.textReadyToSend.items = getReadyToSendDropdownItems();
        const hasIntercompany = LicenseTester.testClass("com.tms.client.loadmaster.dsp.PanelOrderIntercompany"); //isIntercompanyEnabled(); TODO replace after PR 270900 is complete?
        this.checkboxIntercompany.visible = hasIntercompany;
        if (!CompanySettings.getSingleton().isAsset()) {
            this.tableOtherCharges.removeColumn(this.tableOtherCharges.columns.find(col => "columnHeaderDriver" === col.headingCell.id));
        }
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.panelTaxes.visible = true;
        }
        this.generateTransmissionLogQuickInfo(this.textboxTransmitted, false);
        this.setupPartnerIdTooltipContent();
    }
    /** This is an event handler for the afterExecution event of sourceEdiBilling. */
    sourceEdiBillingAfterExecution(event: DataSourceExecutionEvent) {
        const row = event.data;

        this.sourceEdiMapError.search({ parent_id: this.mainDataSource.activeRow?.get("id"), parent_tablename: "edi_billing" });
        this.setTooltipContent(this.txtCorrectionIndicator, row);
        this.txtCorrectionIndicator.addLookupModelSelectionListener(() => {
            this.setTooltipContent(this.txtCorrectionIndicator, row);
        });

        this.setTooltipContent(this.txtServiceCode, row);

        this.txtServiceCode.addLookupModelSelectionListener(() => {
            this.setTooltipContent(this.txtServiceCode, row);
        });

        setBatchCodeTooltip(this.txtBatch, row, "I", Alignment.TOP);
    }

    styleTab(tab: Tab) {
        const panelTabStuff = new Panel();
        panelTabStuff.setProps({ fillRow: true, rowBreak: false, marginTop: 0 });

        if (tab.id == "tabBalanceDue") {
            if (tab.heading != null && tab.headingLabel != null) {
                tab.heading.color = "subtle.light";
                tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: true })

                if (tab["tabHeaderPanel"] == null) {
                    tab["tabHeaderPanel"] = panelTabStuff;
                    const lb = new Label({
                        fontBold: true, text: "", fillRow: false,
                        color: "primary", rowBreak: false, marginLeft: 5,
                        id: "labelErrorCount"
                    });
                    panelTabStuff.add(lb);
                }
            }

            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["tabHeaderPanel"] == null) {
                tab["tabHeaderPanel"] = panelTabStuff;
            }
            const bt = new Button({
                color: "primary",
                imageName: ImageName.pencil,
                borderWidth: 0,
                id: "buttonEdit",
                onClick: async (event) => {
                    this.buttonEditOnClick(event);
                },
                rowBreak: false
            });
            panelTabStuff.add(bt);
            tab.heading.insert(tab["tabHeaderPanel"], 1);
        } else {
            if (tab.heading != null && tab.headingLabel != null) {
                tab.heading.color = "subtle.light";
                tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
                if (tab["countLabel"] == null) {
                    tab["countLabel"] = new Label({
                        fontBold: true, text: "0", fillRow: true,
                        color: "primary", rowBreak: false, marginLeft: 5,
                        id: "labelErrorCount"
                    });
                    tab.heading.insert(tab["countLabel"], 1);
                }
            }
        }
    }
    /** This is an event handler for the onClick event of buttonSave. */
    async buttonEditOnClick(event: ClickEvent) {
        const props = {
            tabErrorsVisible: false,
            sourceEdiBillingMode: DataSourceMode.UPDATE,
            textReadyToSendEnabled: true,
            txtCorrectionIndicatorEnabled: true,
            txtServiceCodeEnabled: true,
            checkBalanceDueEnabled: true,
            txtBatchEnabled: true,
            billingId: this.sourceEdiBilling.activeRow.get("id"),
            mainDataSource: this.mainDataSource,
            tabBalanceDueExpanded: true,
            switchTransmitEnabled: true,
            ratesEnabled: false,
            ocEnabled: false
        };
        openEdiBalanceDueSlideout(props);
    }

    async onSave() {
        try {
            if (this.sourceEdiBilling.activeRow?.get("batch") > 0) {
                this.sourceEdiBilling.activeRow?.set("batch", 0);
            }
            const row = await this.sourceEdiBilling.post();
            if (this.switchTransmit.checked) {
                await Api.post("lme/datafusion/transmit-edi-bill", { edi_billing_id: this.sourceEdiBilling.activeRow.get("id") }).then(async (response) => {
                    if (response.data.length > 0) {
                        const message = response.data[0].error;
                        if (message) {
                            Snackbar.showWarningSnackbar(message);
                        }
                        else {
                            if (response.data.length > 0 && response.data[0].transmitted_batch != null) {
                                this.newBatch = response.data[0].transmitted_batch;
                            }
                        }
                    }
                }
                );
            }
        }
        catch (error) {
            return;
        }
        this.slideOut();
    }

    async corrIndBeforeLookupModelSearch(event) {
        const ediVersion = this.sourceEdiBilling.activeRow?.get("version", "004010");
        event.filter.version_input = ediVersion;
        event.filter.element_input = "202";
    }
    setTooltipContent(component: Component, row: ModelRow) {
        if (component === null || component === undefined || component.id === null || row === undefined || row === null) return;
        switch (component.id) {
            case "txtCorrectionIndicator":
                if (row.get("correction_ind")) {
                    CommonTooltips.setTooltipContentLayout(component, null, "lme/ar/CorrIndQuickInfo", null, { "version": row.get("version"), "standard_code": row.get("correction_ind") });
                    component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                }
                break;
            case "txtServiceCode":
                if (row.get("service_code")) {
                    CommonTooltips.setTooltipContentLayout(component, null, "lme/ar/ServiceCodeQuickInfo", null, { "version_input": row.get("version"), "element_input": "56", "standard_code": row.get("service_code") });
                    component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                }
                break;
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of txtServiceCode. */
    txtServiceCodeBeforeLookupModelSearch(event) {
        const ediVersion = this.sourceEdiBilling.activeRow?.get("version", "004010");
        event.filter.version_input = ediVersion;
        event.filter.element_input = "56";
    }

    /** This is an event handler for the afterExecution event of sourceEdiMapError. */
    sourceEdiMapErrorAfterExecution(event: DataSourceExecutionEvent) {
        const errorCount = this.sourceEdiMapError.data.length;
        if (this.tabErrors != null && this.tabErrors["countLabel"] != null) {
            this.tabErrors["countLabel"].text = errorCount;
            if (errorCount > 0) {
                this.tabErrors["countLabel"].color = "error";
            }
        }
    }
    public async loadEdiMapError(id: string) {
        const layoutErrors = this.findComponentById("layoutMapErrors") as EdiMapErrors;
        const tableErrors = this.findComponentById("tableError") as Table;

        // Fetch errors from the source
        const errors = await layoutErrors.sourceEdiMapError.search({ parent_id: id, parent_tablename: "edi_billing" });

        // Clear existing rows in the table
        tableErrors.clearRows();

        // Update the error count label
        const count = errors.modelRows.length;
        this.tabErrors["countLabel"].text = count;
        this.tabErrors["countLabel"].color = count > 0 ? "error" : "primary";

        // Populate the table with the errors
        errors.modelRows.forEach(error => {
            tableErrors.addRow(error, { mode: TableRowMode.NONE }, { display: true });
        });


    }

    public async loadOtherCharges(id: string) {
        const tableOtherCharges = this.findComponentById("tableOtherCharges") as Table;

        // Fetch errors from the source
        const charges = await this.sourceOtherChargeEdi.search({ invoice_id: id });

        // Clear existing rows in the table
        tableOtherCharges.clearRows();

        // Populate the table with the errors
        charges.modelRows.forEach(error => {
            tableOtherCharges.addRow(error, { mode: TableRowMode.NONE }, { display: true });
        });


    }

    public async loadTaxCharges(id: string) {
        const tableOtherCharges = this.findComponentById("tableTaxCharges") as Table;

        // Fetch errors from the source
        const charges = await this.sourceExcisetaxChargeEdi.search({ invoice_id: id });

        // Clear existing rows in the table
        tableOtherCharges.clearRows();

        // Populate the table with the errors
        charges.modelRows.forEach(error => {
            tableOtherCharges.addRow(error, { mode: TableRowMode.NONE }, { display: true });
        });


    }

    buttonExpandChargesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.chargePanelExpanded) this.panelOtherChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 90
        }); else this.panelOtherChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 0
        });
        this.chargePanelExpanded = !this.chargePanelExpanded;
    }

    buttonExpandTaxesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.taxPanelExpanded) this.panelTaxChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandTaxes.imageRotation = 90
        }); else this.panelTaxChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandTaxes.imageRotation = 0
        });
        this.taxPanelExpanded = !this.taxPanelExpanded;
    }

    public get openedFromSlideout(): boolean {
        return this._openedFromSlideout;
    }
    public set openedFromSlideout(value: boolean) {
        this._openedFromSlideout = value;
    }
    private _labelTransStatusOnDataDisplay() {
        this.generateTransmissionLogQuickInfo(this.textboxTransmitted, false);
    }
    private generateTransmissionLogQuickInfo(component: Component, isInbound: boolean) {
        component.tooltipDynamicContent = async () => {
            if (this.mainDataSource?.activeRow?.get("batch") > "0") {
                const layout = await Layout.getLoadedLayout("lme/datafusion/TransmissionStatusQuickInfo") as TransmissionStatusQuickInfo;
                if (layout.sourceEdiLog !== null) {
                    const searchParameters = isInbound ? {
                        direction: "I",
                        batch: this.mainDataSource.activeRow.get("batch"),
                        func_sender_id: this.mainDataSource.activeRow.get("partner_id"),
                        sender_id: this.mainDataSource.activeRow.get("alt_partner_id"),
                        func_version: this.mainDataSource.activeRow.get("version"),
                        transaction_type: 'I'
                    } :
                        {
                            direction: "O",
                            batch: this.mainDataSource.activeRow.get("batch"),
                            func_receiver_id: this.mainDataSource.activeRow.get("partner_id"),
                            receiver_id: this.mainDataSource.activeRow.get("alt_partner_id"),
                            func_version: this.mainDataSource.activeRow.get("version"),
                            transaction_type: 'I'
                        }
                    await layout.sourceEdiLog.search(searchParameters);
                }
                return layout;
            }
            return null;
        };
    }

    /** This is an event handler for the onClick event of buttonExpand. */
    buttonExpandOnClick(event: ClickEvent) {
        if (this._panelEdiBalanceDueExpanded === true) {
            this.collapse();
        }
        else {
            this._panelEdiBalanceDueExpanded = true;
            this.setChevronImageRotation();
            this.expand();
        }
    }
    private setChevronImageRotation() {
        this.buttonExpand.imageRotation = this._panelEdiBalanceDueExpanded ? 0 : 90;
    }
    public override collapse(options?: TransitionOptions): Promise<any> {
        this._panelEdiBalanceDueExpanded = false;
        this.setChevronImageRotation();
        return this.panelCollapsible.collapse({ speed: 400, ...options });
    }

    public override async expand(options?: TransitionOptions) {
        this.panelCollapsible.expand({ speed: 400, ...options }).then(() => {
            // this.focusFirstSearchField();
        });
    }

    /** This is an event handler for the onClick event of buttonReload. */
    buttonReloadOnClick(event: ClickEvent) {
        reloadEdiBilling(this.sourceEdiBilling);
    }

    openRawDataOnClick(event: ClickEvent) {
        const invoiceId = this.sourceEdiBilling.activeRow?.get("id");
        const orderId = this.sourceEdiBilling.activeRow?.get("order_id");
        const templateType = this.sourceEdiBilling.activeRow?.get("edi_template_type");
        EdiRawDataSlideout.openSlideout({ title: `Balance Due - ${orderId}`, invoiceId, templateType });
    }

    /** This is an event handler for the onClick event of switchTransmit. */
    switchTransmitOnClick(event: ClickEvent) {
        const textboxes = [
            this.txtShipmentId,
            this.textboxBlnum,
            this.textboxConsigneeRefNo,
            this.textboxCommodity,
            this.textboxCommodityDescr,
            this.textboxWeight,
            this.textboxPieces
        ];

        const isChecked = this.switchTransmit.checked;

        textboxes.forEach(textbox => {
            textbox.enabled = isChecked;
        });
    }

    public get newBatch(): number {
        return this._newBatch;
    }
    public set newBatch(value: number) {
        this._newBatch = value;
    }

    /** This is an event handler for the onValidate event of sourceEdiBilling. */
    sourceEdiBillingOnValidate(event: DataSourceValidationEvent) {
        if (this.sourceEdiBilling.activeRow?.get("batch") > 0) {
            this.sourceEdiBilling.activeRow?.set("batch", 0);
        }
    }

    displayPartnerName() {
        const row = this.sourceEdiBilling.activeRow;
        this.textPartnerId.text = row?.get("edibilling_profile.partner_name");
    }

    private setupPartnerIdTooltipContent() {
        this.textPartnerId.tooltipDynamicContent = async () => {
            const row = this.sourceEdiBilling.activeRow;
            const partnerID = row?.get("edibilling_profile.partner_id");
            const altPartnerID = row?.get("edibilling_profile.alt_partner_id");
            const version = row?.get("edibilling_profile.version");
            const direction = row?.get("edibilling_profile.direction");
            const transaction_set = row?.get("edibilling_profile.transaction_set");
            const batch = row?.get("batch");
            if (partnerID != null && altPartnerID != null && version != null && direction != null && transaction_set != null) {
                const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileBillingQuickInfo") as EdiProfileBillingQuickInfo;
                await layout.sourceEdibillingProfile?.search({ partner_id: partnerID, alt_partner_id: altPartnerID, version: version, direction: direction, transaction_set: transaction_set, batch: batch ?? 0 });
                return layout;
            }
            return null;
        };
    }

    buttonOpenInvoiceOnClick(event: ClickEvent) {
        if (this.sourceEdiBilling.activeRow) {
            ViewEdiBillingDetail.navigateTo({ key: this.sourceEdiBilling.activeRow.get("id"), newTab: true });
        }
    }
}
